/* The events list, and capturing one — STORY-011.
 *
 * ── the empty state is a decision, not a detail ──────────────────────────────
 * The empty workspace is the highest-churn screen in the product. It carries
 * ONE line of orientation — why Events are the starting point rather than
 * People or Tasks — and then a single action.
 *
 * Rejected while writing the story: a bare one-liner (no help for somebody who
 * has not grasped the model), a worked example row (a fake row in a real list
 * invites a click), and several entry points (inviting a teammate is blocked on
 * Free, so it would greet a new user with an upgrade prompt).
 *
 * ── the form asks for a name and stops ───────────────────────────────────────
 * Everything else is optional and saves later, from the Event itself. That is
 * the story: an idea is captured the moment somebody has it rather than being
 * lost while they go and find a date.
 *
 * ── the default does the work — STORY-040 ───────────────────────────────────
 * The list opens on active events, soonest first, with undated ideas last. Most
 * people should never touch a filter: "hide inactive" is not a feature when it
 * is already what you see.
 *
 * Filtering, sorting and paging all happen on the SERVER, and the filters live
 * in the URL. A workspace with hundreds of events must not send them all to a
 * phone to be filtered there, and a view worth returning to is a view worth
 * being able to link.
 */

import type { Event } from '@eventuley/contracts';
import { formatDate, zoneCity } from '@eventuley/i18n';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { ApiError, NetworkError } from '../../../shared/api/client.js';
import { useI18n, useTranslate } from '../../../shared/i18n/index.js';
import { Button, Callout, TextField } from '../../../shared/ui/index.js';
import { useMembers } from '../../organisations/index.js';
import { useVenues } from '../../venues/index.js';
import { useCreateEvent, useEvents } from '../api/index.js';
import { ActiveFilters } from '../components/active-filters.js';
import { EventFilters } from '../components/event-filters.js';
import { EventStatusBadge } from '../components/event-status-badge.js';
import {
  activeFilterCount,
  type EventsSearch,
  PAGE_SIZE,
  toQuery,
  toSearchState,
} from '../filters.js';

/* A refusal the NAME field can explain. Anything else belongs in the callout,
 * which is what stops a lost Membership rendering as "give it a name". */
const isFieldRefusal = (error: unknown) =>
  error instanceof ApiError && error.code === 'validation_failed';

export function EventsScreen() {
  const t = useTranslate();
  const navigate = useNavigate();
  const { organisationId = '' } = useParams({ strict: false });
  const search = useSearch({ strict: false }) as EventsSearch;

  const query = toQuery(search);
  /* `isLoading`, never `isPending`.
   *
   * A disabled query stays `pending` forever in TanStack Query v5 — it is
   * waiting for permission to run, not for a response — so a cold load of a
   * URL with every status unticked rendered "Loading…" and never reached the
   * "nothing matches" state underneath it. `isLoading` is pending AND
   * fetching, which is the question this screen is actually asking. */
  const { data, error, isLoading } = useEvents(organisationId, query);
  const create = useCreateEvent(organisationId);

  /* The filter controls need names for the ids they offer. Both lists are
   * small and already cached by other screens. */
  const { data: venueList } = useVenues(organisationId);
  const { data: memberList } = useMembers(organisationId);

  const [capturing, setCapturing] = useState(false);
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');

  const filters = activeFilterCount(search);

  /* Somebody unticked every status. The query never runs — no status matches no
   * Event. */
  const wantsNothing = search.status?.length === 0;

  /* And the previous page is dropped when it does not run.
   *
   * The list keeps the last result on screen while the next one loads, so
   * filtering does not blank the page and bounce the scroll. That is right for
   * a query that is FETCHING and wrong for one that will never run: unticking
   * every status left the previous page sitting there, so a filter that
   * matches nothing showed a full list. */
  const events = wantsNothing ? [] : (data?.events ?? []);
  const total = wantsNothing ? 0 : (data?.total ?? 0);

  const page = search.page ?? 1;
  /* One-based, because it is read by a person rather than used as an offset. */
  const firstOnPage = (page - 1) * PAGE_SIZE + 1;

  /* THE distinction on this screen: "you have no events" and "you have events
   * but none match" are different statements, and confusing them is alarming.
   * Filters being active is what tells them apart, and the count is honest
   * because it comes from the same query as the page. */
  const emptyBecauseFiltered = (filters > 0 || wantsNothing) && events.length === 0;

  /* `replace` rather than a push: adjusting a filter is refining one view, not
   * arriving at a new one, and a back button that walks back through six
   * checkbox clicks is a back button nobody uses. */
  const replaceSearch = (next: EventsSearch) => {
    void navigate({
      to: '/orgs/$organisationId/events',
      params: { organisationId },
      search: toSearchState(next),
      replace: true,
    });
  };

  /* Refused on the client too, so the reader is told before a round trip — and
   * refused again by the server, which is the one that decides. */
  const timeWithoutDate = time !== '' && date === '';

  return (
    <>
      <div className="page-head">
        <h1>{t('events.title')}</h1>
        {events.length > 0 && !capturing ? (
          <Button onClick={() => setCapturing(true)}>{t('events.new')}</Button>
        ) : null}
      </div>

      {error ? (
        <Callout tone="danger">
          {error instanceof NetworkError ? t('error.network') : t('error.unexpected')}
        </Callout>
      ) : null}

      <EventFilters
        search={search}
        venues={(venueList?.venues ?? []).map((venue) => ({ id: venue.id, name: venue.name }))}
        /* The whole team, not the reader. Mapping the signed-in user's own
            memberships gave a select with exactly one option — yourself —
            because `memberships` is unique per (user, organisation), so a
            Member could not filter by a colleague at all. */
        organisers={(memberList?.members ?? []).map((member) => ({
          id: member.userId,
          name: member.displayName,
        }))}
        onChange={replaceSearch}
      />

      <ActiveFilters search={search} onChange={replaceSearch} />

      {capturing ? (
        <section className="settings__section">
          <h2>{t('events.capture.heading')}</h2>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (timeWithoutDate) return;
              create.mutate(
                {
                  name,
                  /* Empty is absent, not empty-string: the contract wants a date
                   * or nothing, and "" is neither. */
                  ...(date === '' ? {} : { date }),
                  ...(time === '' ? {} : { time }),
                },
                {
                  onSuccess: () => {
                    setName('');
                    setDate('');
                    setTime('');
                    setCapturing(false);
                  },
                },
              );
            }}
            noValidate
          >
            <TextField
              label={t('events.capture.name_label')}
              value={name}
              onChange={(event) => setName(event.target.value)}
              error={isFieldRefusal(create.error) ? t('events.capture.name_required') : undefined}
            />
            {/* Optional, and said so — the whole point is that a name is enough. */}
            <p className="field__hint">{t('events.capture.rest_optional')}</p>

            <TextField
              label={t('events.capture.date_label')}
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
            <TextField
              label={t('events.capture.time_label')}
              type="time"
              value={time}
              onChange={(event) => setTime(event.target.value)}
              error={timeWithoutDate ? t('events.capture.time_needs_date') : undefined}
            />

            {/* Every refusal says something. The field error below covers a
                failed validation; this covers the rest — a lost Membership, a
                rate limit, an expired session, a server fault — each of which
                used to leave the button un-busy and the form looking inert. */}
            {create.error && !isFieldRefusal(create.error) ? (
              <Callout tone="danger">
                {create.error instanceof NetworkError
                  ? t('error.network')
                  : create.error instanceof ApiError && create.error.code === 'not_found'
                    ? t('events.capture.gone')
                    : create.error instanceof ApiError && create.error.code === 'rate_limited'
                      ? t('error.rate_limited')
                      : t('error.unexpected')}
              </Callout>
            ) : null}

            <div className="btn-row">
              <Button type="submit" busy={create.isPending} disabled={timeWithoutDate}>
                {t('events.capture.submit')}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  create.reset();
                  setCapturing(false);
                }}
              >
                {t('events.capture.cancel')}
              </Button>
            </div>
          </form>
        </section>
      ) : null}

      {isLoading ? <p className="field__hint">{t('common.loading')}</p> : null}

      {/* Nothing matches, and something is filtering. Never the empty-workspace
          state, which would tell somebody their records are gone. */}
      {!isLoading && emptyBecauseFiltered && !error ? (
        <section className="settings__section">
          <h2>{t('events.filters.none_match.title')}</h2>
          <p className="field__hint">{t('events.filters.none_match.body')}</p>
          {/* The same "filters only" rule as the chip bar: an ordering chosen
              elsewhere survives clearing what is HIDING things. */}
          <Button
            onClick={() =>
              replaceSearch({
                ...(search.sort === undefined ? {} : { sort: search.sort }),
                ...(search.dir === undefined ? {} : { dir: search.dir }),
              })
            }
          >
            {t('events.filters.clear')}
          </Button>
        </section>
      ) : null}

      {/* `isLoading` guards it, because `data` is undefined on the first render
          and a workspace that HAS events would otherwise be told it has none —
          then flip to the list a moment later. */}
      {!isLoading && !emptyBecauseFiltered && events.length === 0 && !capturing && !error ? (
        <section className="settings__section">
          <h2>{t('events.empty.title')}</h2>
          {/* One line of orientation: why Events rather than People or Tasks. */}
          <p className="field__hint">{t('events.empty.body')}</p>
          <Button onClick={() => setCapturing(true)}>{t('events.empty.action')}</Button>
        </section>
      ) : null}

      {events.length > 0 ? (
        <>
          <ul className="row-list">
            {events.map((event) => (
              <EventRow key={event.id} event={event} organisationId={organisationId} />
            ))}
          </ul>

          {/* Paged rather than fetched whole — a workspace with hundreds of
              events must not send them all to a phone.

              One page at a time, with a range rather than a running total. An
              earlier version said "Show more" and then REPLACED the page: the
              rows it promised to add were the rows it removed, and the count
              beside it read "Showing 26 of 40" above a single row. A button
              that appends and a URL that names one page cannot both be true,
              and the URL wins — a link to page 2 has to show page 2. */}
          <p className="field__hint" role="status">
            {t('events.list.showing', {
              from: String(firstOnPage),
              to: String(firstOnPage + events.length - 1),
              total: String(total),
            })}
          </p>

          {total > PAGE_SIZE ? (
            <div className="btn-row">
              <Button
                variant="ghost"
                disabled={page <= 1}
                onClick={() => replaceSearch({ ...search, page: page - 1 })}
              >
                {t('events.list.previous')}
              </Button>
              <Button
                variant="ghost"
                disabled={firstOnPage + events.length > total}
                onClick={() => replaceSearch({ ...search, page: page + 1 })}
              >
                {t('events.list.next')}
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
    </>
  );
}

function EventRow({ event, organisationId }: { event: Event; organisationId: string }) {
  const t = useTranslate();
  const { formatLocale } = useI18n();

  return (
    <li className="row row--linked">
      <span className="row__main">
        <span className="row__title">
          <Link
            to="/orgs/$organisationId/events/$eventId"
            params={{ organisationId, eventId: event.id }}
          >
            {event.name}
          </Link>
        </span>
        {/* "No date yet" is SHOWN rather than left blank: it is a state of
            planning worth seeing at a glance, and a gap reads as a bug.

            Formatted in UTC deliberately. This is a CALENDAR date — the 14th is
            the 14th at the Venue — so converting it into any reader's zone
            would move it across midnight for somebody. The zone here decides
            only how the words are ordered, which the locale already does. */}
        <span className="row__meta">
          {event.date
            ? formatDate(new Date(`${event.date}T00:00:00Z`), {
                locale: formatLocale,
                timeZone: 'UTC',
              })
            : t('events.no_date')}
          {/* `HH:MM` from a `HH:MM:SS` column. Seconds on a start time are
              precision nobody entered. */}
          {event.time ? ` · ${event.time.slice(0, 5)}` : ''}
          {/* The zone the time is read in, and it is the VENUE's — an Event's
              time belongs where it happens, and "19:30" alone gives a reader in
              another country no way to tell whose evening it is. Shown only
              beside a time, because a date carries no zone. */}
          {event.time ? ` ${zoneCity(event.timezone)}` : ''}
        </span>
        {/* The Venue — STORY-040 lists it beside the date, because "where" is
            half of what somebody scans a list of events for. */}
        <span className="row__meta">
          {event.venue ? event.venue.name : t('events.list.venue_unknown')}
        </span>
      </span>
      <EventStatusBadge status={event.status} />
    </li>
  );
}
